/**
 * @fileoverview
 * FAQAccordion displays frequently asked questions in expandable
 * accordion format with smooth animations and keyboard navigation.
 */

"use client";

import type { MessageDescriptor } from "@lingui/core";
import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { useLingui } from "@lingui/react/macro";

import { SectionEyebrow } from "@/components/marketing/homepage/primitives";

/**
 * Individual FAQ item structure.
 */
type FaqItem = {
  /**
   * Question text
   */
  question: string | MessageDescriptor;
  /**
   * Answer text
   */
  answer: string | MessageDescriptor;
};

/**
 * Props for FAQAccordion component.
 */
type FaqAccordionProps = {
  /**
   * Array of FAQ items (question/answer pairs)
   */
  faqs: readonly FaqItem[];
  /**
   * Optional section title
   */
  title?: string | MessageDescriptor;
  /**
   * Optional subtitle text
   */
  subtitle?: string | MessageDescriptor;
};

/**
 * FAQAccordion displays expandable FAQ list with smooth animations.
 * Single-item open pattern (accordion behavior).
 *
 * Features:
 * - **Accordion Behavior**: Only one item open at a time
 * - **Smooth Animations**: 300ms ease-in-out for open/close
 * - **Keyboard Accessible**: Full keyboard navigation support
 * - **Responsive**: Adapts padding and spacing
 *
 * Interaction:
 * - Click question to toggle answer
 * - Clicking open item closes it
 * - Chevron rotates 90deg when open, 0deg when closed
 *
 * Animation States:
 * - **Open**: max-h-96, opacity 100
 * - **Closed**: max-h-0, opacity 0
 * - **Transition**: 300ms ease-in-out on max-height and opacity
 *
 * Visual Design:
 * - **Background**: Light gray (#F8F9FA)
 * - **Spacing**: Generous padding (20-24 vertical)
 * - **Borders**: Bottom border between items (#E5E7EB)
 * - **Typography**: Bold question, muted gray answer
 *
 * Question Styling:
 * - **Font**: Bold, large (1.25-1.75rem)
 * - **Color**: Dark gray (#1F2937)
 * - **Padding**: 28px vertical
 * - **Hover**: Subtle dark background hover (black/[0.02])
 * - **Focus**: Visible outline on keyboard focus
 *
 * Answer Styling:
 * - **Font**: Regular, medium (1-1.125rem)
 * - **Color**: Medium gray (#4B5563)
 * - **Padding**: 28px vertical bottom, 48px right
 * - **Leading**: Relaxed (1.625 or 1.5)
 *
 * Header Section (Optional):
 * - **Subtitle**: Uppercase, wide tracking, small text, muted
 * - **Title**: Bold, very large (2.5-3.75rem), tight leading
 * - **Spacing**: 16px gap between elements
 * - **Width**: Title max-width 4xl, centered
 *
 * Accessibility:
 * - **ARIA**: aria-expanded, aria-controls, aria-labelledby
 * - **Keyboard**: Tab to navigate, Enter/Space to toggle
 * - **Semantics**: button element for questions, region for answers
 * - **Focus Indicators**: Visible outline on focus
 *
 * Layout:
 * - **Container**: Max width 6xl, centered
 * - **Grid**: Centered, auto margins
 * - **Header**: Optional, above accordion list
 * - **Accordion**: Below header, full width available
 *
 * @param props - Component props
 * @param props.faqs - Array of FAQ items
 * @param props.title - Optional section title
 * @param props.subtitle - Optional subtitle
 * @returns JSX.Element
 *
 * @example
 * ```tsx
 * <FAQAccordion
 *   faqs={[
 *     { question: "How does it work?", answer: "Answer text..." },
 *     { question: "Pricing?", answer: "Answer text..." }
 *   ]}
 *   title="FAQ"
 *   subtitle="Common questions"
 * />
 * ```
 */
export function FAQAccordion({ faqs, title, subtitle }: FaqAccordionProps) {
  const { i18n } = useLingui();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const renderLabel = (value: string | MessageDescriptor) =>
    typeof value === "string" ? value : i18n._(value);

  const toggleItem = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="w-full bg-[#F8F9FA] px-6 py-10 sm:py-14 lg:px-12 lg:py-20" id="faq">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        {(title || subtitle) && (
          <div className="mb-12">
            {subtitle && (
                <SectionEyebrow
                  align="left"
                  backgroundClassName="bg-[#F8F9FA]"
                  label={renderLabel(subtitle)}
                  showLine={false}
                />
              )}
             {title && (
               <h2 className="max-w-4xl font-bold text-4xl text-black leading-tight md:text-5xl">
                 {renderLabel(title)}
               </h2>
             )}
          </div>
        )}

        {/* Accordion List */}
        <div className="mx-auto max-w-5xl">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;

            return (
              <div className="border-[#E5E7EB] border-b" key={`faq-${index.toString()}`}>
                {/* Question Trigger */}
                <button
                  aria-controls={`faq-answer-${index}`}
                  aria-expanded={isOpen}
                  className="flex w-full cursor-pointer items-center justify-between py-7 text-left transition-colors hover:bg-black/[0.02] md:py-7"
                  onClick={() => toggleItem(index)}
                  type="button"
                >
                  <span className="pr-6 font-medium text-[#1F2937] text-lg leading-relaxed md:text-xl">
                    {renderLabel(faq.question)}
                  </span>
                  <ChevronRight
                    aria-hidden="true"
                    className={`size-6 flex-shrink-0 text-[#9CA3AF] transition-transform duration-300 ease-in-out ${
                      isOpen ? "rotate-90" : "rotate-0"
                    }`}
                  />
                </button>
                {/* Answer Content */}
                <div
                  aria-labelledby={`faq-question-${index}`}
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                  }`}
                  id={`faq-answer-${index}`}
                  role="region"
                >
                  <div className="pr-12 pb-7 text-[#4B5563] text-base leading-relaxed md:text-lg">
                    {renderLabel(faq.answer)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
